import type { Cell, Network } from "./types";
import { COILS } from "./ladder-utils";

export interface TimerState {
  et: number;
  q: boolean;
  prev: boolean;
}
export interface CounterState {
  cv: number;
  q: boolean;
  prev: boolean;
}

/** PLC data memory: bits (I/Q/M), words (MW/IW/QW), timers and counters. */
export class Memory {
  bits: Record<string, boolean> = {};
  words: Record<string, number> = {};
  edges: Record<string, boolean> = {};
  timers: Record<string, TimerState> = {};
  counters: Record<string, CounterState> = {};

  bit(addr: string): boolean {
    return Boolean(this.bits[addr.toUpperCase().trim()]);
  }
  setBit(addr: string, v: boolean) {
    this.bits[addr.toUpperCase().trim()] = v;
  }
  word(addr: string): number {
    const a = addr.toUpperCase().trim();
    if (!a) return 0;
    if (/^-?\d+(?:\.\d+)?$/.test(a)) return Number(a);
    return this.words[a] ?? 0;
  }
  setWord(addr: string, v: number) {
    const a = addr.toUpperCase().trim();
    if (!a || !Number.isFinite(v)) return;
    this.words[a] = v;
  }
  clearNonRetentive() {
    for (const k of Object.keys(this.bits)) if (!k.startsWith("I")) this.bits[k] = false;
    this.words = {};
    this.timers = {};
    this.counters = {};
  }
}

export function parsePreset(s: string | undefined, fallback = 1000): number {
  if (!s) return fallback;
  const t = s.trim().toLowerCase();
  const m = /^([\d.]+)\s*(ms|s|m)?$/.exec(t);
  if (!m) return fallback;
  const v = Number(m[1]);
  if (m[2] === "ms") return v;
  if (m[2] === "m") return v * 60000;
  return v * 1000;
}

function contactPasses(cell: Cell, mem: Memory, nid: string, r: number, c: number): boolean {
  const key = `${nid}:${r}:${c}`;
  const v = mem.bit(cell.operand);
  switch (cell.type) {
    case "NO":
      return v;
    case "NC":
      return !v;
    case "P": {
      const prev = Boolean(mem.edges[key]);
      mem.edges[key] = v;
      return v && !prev;
    }
    case "N": {
      const prev = Boolean(mem.edges[key]);
      mem.edges[key] = v;
      return !v && prev;
    }
    case "CMP": {
      const a = mem.word(cell.operand);
      const b = mem.word(cell.b ?? "0");
      const c = mem.word(cell.c ?? "0");
      switch ((cell.op ?? ">").toUpperCase()) {
        case ">":
          return a > b;
        case "<":
          return a < b;
        case ">=":
          return a >= b;
        case "<=":
          return a <= b;
        case "<>":
        case "!=":
          return a !== b;
        case "IN_RANGE":
          return a >= b && a <= c;
        case "OUT_RANGE":
          return a < b || a > c;
        default:
          return a === b;
      }
    }
    default:
      return true;
  }
}

function safeDiv(a: number, b: number) {
  return b === 0 ? 0 : a / b;
}

function rotateLeft(v: number, n: number) {
  const x = v | 0;
  const s = ((n | 0) % 32 + 32) % 32;
  return (x << s) | (x >>> (32 - s || 32));
}

function rotateRight(v: number, n: number) {
  const x = v | 0;
  const s = ((n | 0) % 32 + 32) % 32;
  return (x >>> s) | (x << (32 - s || 32));
}

function runBox(cell: Cell, input: boolean, mem: Memory, dt: number): boolean {
  const name = (cell.operand || "T?").toUpperCase();

  if (cell.type === "TON" || cell.type === "TOF" || cell.type === "TP" || cell.type === "TONR") {
    const pt = parsePreset(cell.preset, 5000);
    const t = (mem.timers[name] ??= { et: 0, q: false, prev: false });
    if (cell.reset && mem.bit(cell.reset)) {
      t.et = 0;
      t.q = false;
    }
    if (cell.type === "TON") {
      if (input) t.et = Math.min(pt, t.et + dt);
      else t.et = 0;
      t.q = input && t.et >= pt;
    } else if (cell.type === "TONR") {
      if (input) t.et = Math.min(pt, t.et + dt);
      t.q = t.et >= pt;
    } else if (cell.type === "TOF") {
      if (input) {
        t.et = 0;
        t.q = true;
      } else if (t.q) {
        t.et = Math.min(pt, t.et + dt);
        if (t.et >= pt) t.q = false;
      }
    } else {
      if (input && !t.prev && t.et === 0) t.q = true;
      if (t.q) {
        t.et = Math.min(pt, t.et + dt);
        if (t.et >= pt) t.q = false;
      }
      if (!input && !t.q) t.et = 0;
    }
    t.prev = input;
    mem.setBit(`${name}.Q`, t.q);
    mem.setWord(`${name}.ET`, Math.round(t.et));
    return t.q;
  }

  if (cell.type === "CTU" || cell.type === "CTD" || cell.type === "CTUD") {
    const pv = Number(cell.preset ?? 5) || 5;
    const counter = (mem.counters[name] ??= { cv: 0, q: false, prev: false });
    if (cell.reset && mem.bit(cell.reset)) counter.cv = 0;
    const rise = input && !counter.prev;
    if (rise) counter.cv += cell.type === "CTD" ? -1 : 1;
    if (cell.type === "CTUD" && cell.b && mem.bit(cell.b)) counter.cv -= 1;
    counter.prev = input;
    counter.q = cell.type === "CTD" ? counter.cv <= 0 : counter.cv >= pv;
    mem.setBit(`${name}.Q`, counter.q);
    mem.setBit(`${name}.QU`, counter.cv >= pv);
    mem.setBit(`${name}.QD`, counter.cv <= 0);
    mem.setWord(`${name}.CV`, counter.cv);
    return counter.q;
  }

  if (cell.type === "SR" || cell.type === "RS") {
    const qAddr = cell.operand || "M0.0";
    const set = input && (cell.preset ? mem.bit(cell.preset) : true);
    const reset = cell.b ? mem.bit(cell.b) : false;
    let q = mem.bit(qAddr);
    if (cell.type === "SR") {
      if (set) q = true;
      if (reset) q = false;
    } else {
      if (reset) q = false;
      if (set) q = true;
    }
    mem.setBit(qAddr, q);
    return q;
  }

  if (cell.type === "BOOL") {
    const a = cell.preset ? mem.bit(cell.preset) : input;
    const b = cell.b ? mem.bit(cell.b) : input;
    const c = cell.c ? mem.bit(cell.c) : false;
    let out = a;
    switch ((cell.op ?? "AND").toUpperCase()) {
      case "AND": out = a && b && (cell.c ? c : true); break;
      case "OR": out = a || b || c; break;
      case "XOR": out = Boolean(Number(a) ^ Number(b) ^ Number(c)); break;
      case "NOT": out = !a; break;
      case "NAND": out = !(a && b && (cell.c ? c : true)); break;
      case "NOR": out = !(a || b || c); break;
      case "XNOR": out = !Boolean(Number(a) ^ Number(b) ^ Number(c)); break;
    }
    if (cell.operand) mem.setBit(cell.operand, out);
    return out;
  }

  if (cell.type === "SELECT") {
    if (!input) return false;
    const selector = cell.reset ? mem.bit(cell.reset) : input;
    const in0 = mem.word(cell.preset ?? "0");
    const in1 = mem.word(cell.b ?? "0");
    mem.setWord(cell.operand || "MW0", selector ? in1 : in0);
    return true;
  }

  if (cell.type === "PROGRAM") {
    // Visual program-control instructions. NOP/LABEL/JMP stay transparent in the
    // single-network teaching scan; RETURN terminates the current branch.
    if ((cell.op ?? "NOP").toUpperCase() === "RETURN") return false;
    return input;
  }

  if (cell.type === "MOVE") {
    if (!input) return false;
    const value = mem.word(cell.preset ?? "0");
    let out = value;
    if ((cell.op ?? "MOVE").toUpperCase() === "SWAP") {
      const v = value | 0;
      out = ((v & 0xff) << 8) | ((v >>> 8) & 0xff);
    }
    mem.setWord(cell.operand || "MW0", out);
    return true;
  }

  if (cell.type === "MATH") {
    if (!input) return false;
    const a = mem.word(cell.preset ?? "0");
    const b = mem.word(cell.b ?? "0");
    let out = a;
    switch ((cell.op ?? "ADD").toUpperCase()) {
      case "ADD": out = a + b; break;
      case "SUB": out = a - b; break;
      case "MUL": out = a * b; break;
      case "DIV": out = safeDiv(a, b); break;
      case "MOD": out = b === 0 ? 0 : a % b; break;
      case "MIN": out = Math.min(a, b); break;
      case "MAX": out = Math.max(a, b); break;
      case "ABS": out = Math.abs(a); break;
      case "NEG": out = -a; break;
      case "INC": out = a + 1; break;
      case "DEC": out = a - 1; break;
      case "SQRT": out = Math.sqrt(Math.max(0, a)); break;
      case "SQR": out = a * a; break;
      case "EXP": out = Math.exp(a); break;
      case "LN": out = a > 0 ? Math.log(a) : 0; break;
      case "LOG": out = a > 0 ? Math.log10(a) : 0; break;
      case "SIN": out = Math.sin(a); break;
      case "COS": out = Math.cos(a); break;
      case "TAN": out = Math.tan(a); break;
      case "ASIN": out = Math.asin(Math.max(-1, Math.min(1, a))); break;
      case "ACOS": out = Math.acos(Math.max(-1, Math.min(1, a))); break;
      case "ATAN": out = Math.atan(a); break;
      default: out = a + b;
    }
    mem.setWord(cell.operand || "MW0", out);
    return true;
  }

  if (cell.type === "WORD_LOGIC") {
    if (!input) return false;
    const a = mem.word(cell.preset ?? "0") | 0;
    const b = mem.word(cell.b ?? "0") | 0;
    let out = a;
    switch ((cell.op ?? "AND").toUpperCase()) {
      case "AND": out = a & b; break;
      case "OR": out = a | b; break;
      case "XOR": out = a ^ b; break;
      case "NOT": out = ~a; break;
      case "SHL": out = a << (b & 31); break;
      case "SHR": out = a >>> (b & 31); break;
      case "ROL": out = rotateLeft(a, b); break;
      case "ROR": out = rotateRight(a, b); break;
    }
    mem.setWord(cell.operand || "MW0", out);
    return true;
  }

  if (cell.type === "LIMIT") {
    if (!input) return false;
    const min = mem.word(cell.preset ?? "0");
    const value = mem.word(cell.b ?? "0");
    const max = mem.word(cell.c ?? "100");
    mem.setWord(cell.operand || "MW0", Math.max(min, Math.min(max, value)));
    return true;
  }

  if (cell.type === "CONVERT") {
    if (!input) return false;
    const value = mem.word(cell.preset ?? "0");
    let out = value;
    switch ((cell.op ?? "ROUND").toUpperCase()) {
      case "ROUND": out = Math.round(value); break;
      case "TRUNC": out = Math.trunc(value); break;
      case "CEIL": out = Math.ceil(value); break;
      case "FLOOR": out = Math.floor(value); break;
      case "NORM_X": {
        const min = mem.word(cell.b ?? "0");
        const max = mem.word(cell.c ?? "100");
        out = max === min ? 0 : (value - min) / (max - min);
        break;
      }
      case "SCALE_X": {
        const min = mem.word(cell.b ?? "0");
        const max = mem.word(cell.c ?? "100");
        out = min + value * (max - min);
        break;
      }
    }
    mem.setWord(cell.operand || "MW0", out);
    return true;
  }

  return input;
}

export interface NetworkPower {
  /** power[r][c] = rail power present at the LEFT edge of column c in row r */
  power: boolean[][];
  /** cellOn[r][c] = element itself conducting / active */
  cellOn: boolean[][];
}

/** Evaluate one network, writing outputs to memory and returning power-flow for monitoring. */
export function evaluateNetwork(net: Network, mem: Memory, dt: number): NetworkPower {
  const rows = net.rows.length;
  const cols = Math.max(0, ...net.rows.map((row) => row.length));
  const power: boolean[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols + 1 }, () => false),
  );
  const cellOn: boolean[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => false),
  );

  for (let r = 0; r < rows; r++) power[r]![0] = true;
  mergeLinks(net, power, 0);

  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const cell = net.rows[r]?.[c] ?? { type: "EMPTY", operand: "" };
      const inp = power[r]![c]!;
      let out = false;
      if (cell.type === "EMPTY") out = inp;
      else if (COILS.includes(cell.type)) {
        applyCoil(cell, inp, mem);
        cellOn[r]![c] = coilActive(cell, inp, mem);
        out = inp;
      } else if (
        cell.type === "TON" ||
        cell.type === "TOF" ||
        cell.type === "TP" ||
        cell.type === "TONR" ||
        cell.type === "CTU" ||
        cell.type === "CTD" ||
        cell.type === "CTUD" ||
        cell.type === "SR" ||
        cell.type === "RS" ||
        cell.type === "BOOL" ||
        cell.type === "SELECT" ||
        cell.type === "PROGRAM" ||
        cell.type === "MOVE" ||
        cell.type === "MATH" ||
        cell.type === "WORD_LOGIC" ||
        cell.type === "LIMIT" ||
        cell.type === "CONVERT"
      ) {
        out = runBox(cell, inp, mem, dt);
        cellOn[r]![c] = out;
      } else {
        const pass = contactPasses(cell, mem, net.id, r, c);
        cellOn[r]![c] = pass;
        out = inp && pass;
      }
      power[r]![c + 1] = out;
    }
    mergeLinks(net, power, c + 1);
  }
  return { power, cellOn };
}

function mergeLinks(net: Network, power: boolean[][], c: number) {
  const rows = net.rows.length;
  for (let pass = 0; pass < rows; pass++) {
    let changed = false;
    for (let r = 0; r < rows - 1; r++) {
      if (!net.links[r]?.[c]) continue;
      const a = power[r]![c]!;
      const b = power[r + 1]![c]!;
      if (a !== b) {
        power[r]![c] = true;
        power[r + 1]![c] = true;
        changed = true;
      }
    }
    if (!changed) break;
  }
}

function applyCoil(cell: Cell, inp: boolean, mem: Memory) {
  const a = cell.operand;
  if (!a) return;
  switch (cell.type) {
    case "COIL":
      mem.setBit(a, inp);
      break;
    case "COIL_NOT":
      mem.setBit(a, !inp);
      break;
    case "SET":
      if (inp) mem.setBit(a, true);
      break;
    case "RESET":
      if (inp) mem.setBit(a, false);
      break;
  }
}

function coilActive(cell: Cell, inp: boolean, mem: Memory) {
  if (cell.type === "SET" || cell.type === "RESET") return inp;
  return mem.bit(cell.operand);
}
